import type {
    AnalyzeResult,
    GeneratedImportJobSummary,
    ImportJobStatus,
    LatestGeneratedImportJobResult,
    ProcessResult,
    StaffImportGridRow,
    StaffImportRow,
    ValidationReport,
} from '../types/staffImport';

const MOCK_LATENCY_MS = 400;
const MOCK_POLL_INTERVAL_MS = 200;

const delay = (ms: number = MOCK_LATENCY_MS) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

const createMockStaffRows = (count: number): StaffImportRow[] =>
    Array.from({ length: count }, (_, index) => {
        const staffNumber = index + 1;
        return {
            firstName: 'Staff',
            middleName: '',
            lastName: `Member ${staffNumber}`,
            role: staffNumber % 3 === 0 ? 'Limited Staff' : 'Staff',
            email: `staff${staffNumber}@example.com`,
            employeeId: `teacher-${staffNumber}`,
            gender: '',
            street: '',
            city: '',
            state: '',
            zipcode: '',
            country: '',
            birthdate: '',
            phoneNumber: `(555) 555-${String(staffNumber).padStart(4, '0')}`,
            onlineLink: '',
        };
    });

const getScenario = (fileName: string) => {
    const lowerName = fileName.toLowerCase();
    if (lowerName.includes('auth-error')) {
        return 'auth-error';
    }
    if (lowerName.includes('empty')) {
        return 'empty';
    }
    if (lowerName.includes('valid')) {
        return 'valid';
    }
    if (lowerName.includes('validation-error')) {
        return 'validation-error';
    }
    return 'default';
};

const buildValidationReport = (
    rows: StaffImportRow[],
    scenario: string,
): ValidationReport => {
    if (scenario === 'validation-error') {
        return {
            rowCount: rows.length,
            validRowCount: Math.max(rows.length - 2, 0),
            invalidRowCount: Math.min(2, rows.length),
            issues: [
                {
                    rowNumber: 2,
                    field: 'email',
                    code: 'invalid_email',
                    message: 'Email address is not valid.',
                    value: 'not-an-email',
                },
                {
                    rowNumber: 4,
                    field: 'firstName',
                    code: 'required',
                    message: 'First name is required.',
                    value: '',
                },
            ],
        };
    }

    return {
        rowCount: rows.length,
        validRowCount: rows.length,
        invalidRowCount: 0,
        issues: [],
    };
};

export type CreateImportJobResult = {
    jobId: string;
    uploadTarget: {
        url: string;
        contentType: string;
    };
    uploadReadyTarget: {
        url: string;
        contentType: string;
    };
};

export const createImportJob = async (
    fileName: string,
    _options: { businessName: string },
): Promise<CreateImportJobResult> => {
    await delay(150);
    return {
        jobId: `mock-job-${Date.now()}`,
        uploadTarget: {
            url: 'https://mock-s3.example.com/upload',
            contentType: 'text/csv',
        },
        uploadReadyTarget: {
            url: 'https://mock-s3.example.com/upload-ready',
            contentType: 'application/octet-stream',
        },
    };
};

export const uploadImportJobArtifacts = async (
    _job: CreateImportJobResult,
    _file: File,
    _signal?: AbortSignal,
): Promise<void> => {
    await delay(150);
};

export const uploadToPresignedUrl = async (
    _url: string,
    _file: File,
    _contentType: string,
    _signal?: AbortSignal,
): Promise<void> => {
    await delay(150);
};

export type StartWebCrawlResult = {
    jobId: string;
    importType: string;
};

export const startWebCrawl = async (
    _url: string,
    _options: { businessName: string },
): Promise<StartWebCrawlResult> => {
    await delay(150);
    return {
        jobId: `mock-web-crawl-${Date.now()}`,
        importType: 'staff',
    };
};

let latestGeneratedImportJob: LatestGeneratedImportJobResult = { job: null };

export const __setLatestGeneratedImportJobForTests = (
    job: GeneratedImportJobSummary | null,
) => {
    latestGeneratedImportJob = { job };
};

export const __resetLatestGeneratedImportJobForTests = () => {
    latestGeneratedImportJob = { job: null };
};

export const getLatestGeneratedImportJob = async (_options: {
    businessName: string;
    signal?: AbortSignal;
}): Promise<LatestGeneratedImportJobResult> => {
    await delay(50);
    return latestGeneratedImportJob;
};

export const pollImportJobStatus = async (
    _jobId: string,
    options: {
        businessName: string;
        fileName?: string;
        importType?: string;
        signal?: AbortSignal;
    },
): Promise<ImportJobStatus> => {
    await delay(MOCK_POLL_INTERVAL_MS);

    const scenario = getScenario(options.fileName || '');

    if (scenario === 'auth-error') {
        return {
            jobId: _jobId,
            status: 'failed',
            updatedAt: new Date().toISOString(),
            rowCount: null,
            errorCount: null,
            error: 'Only business owners can manage import jobs.',
        };
    }

    if (scenario === 'empty') {
        return {
            jobId: _jobId,
            status: 'ready_for_review',
            updatedAt: new Date().toISOString(),
            rowCount: 0,
            errorCount: 0,
            error: null,
        };
    }

    const rowCount =
        scenario === 'valid' ? 32 : scenario === 'validation-error' ? 8 : 8;

    return {
        jobId: _jobId,
        status: 'ready_for_review',
        updatedAt: new Date().toISOString(),
        rowCount,
        errorCount: scenario === 'validation-error' ? 2 : 0,
        error: null,
    };
};

export const fetchImportJobData = async (
    _jobId: string,
    options: {
        businessName: string;
        fileName?: string;
        importType?: string;
        signal?: AbortSignal;
    },
): Promise<AnalyzeResult> => {
    await delay();

    const scenario = getScenario(options.fileName || '');

    if (scenario === 'auth-error') {
        throw new Error('Only business owners can manage import jobs.');
    }

    const rowCount = scenario === 'empty' ? 0 : scenario === 'valid' ? 32 : 8;
    const rows = createMockStaffRows(rowCount);
    const validation = buildValidationReport(rows, scenario);

    return {
        jobId: _jobId,
        rows,
        validation,
        existingBusinessEmails: [],
    };
};

export const uploadCsv = async (
    file: File,
    options: { businessName: string },
): Promise<AnalyzeResult> => {
    const job = await createImportJob(file.name, {
        businessName: options.businessName,
    });
    await uploadImportJobArtifacts(job, file);

    let status = await pollImportJobStatus(job.jobId, {
        businessName: options.businessName,
        fileName: file.name,
    });

    while (status.status === 'processing' || status.status === 'uploaded') {
        await delay(MOCK_POLL_INTERVAL_MS);
        status = await pollImportJobStatus(job.jobId, {
            businessName: options.businessName,
            fileName: file.name,
        });
    }

    if (status.status === 'failed') {
        throw new Error(status.error || 'Import processing failed.');
    }

    return fetchImportJobData(job.jobId, {
        businessName: options.businessName,
        fileName: file.name,
    });
};

export const processImport = async (
    fileName: string,
    rows: StaffImportGridRow[],
    _options: { businessName: string },
): Promise<ProcessResult> => {
    await delay();

    return {
        status: 'success',
        importedCount: rows.length,
        failedCount: 0,
        fileName,
        results: rows.map((row) => ({
            rowId: row.id,
            status: 'success',
            peopleId: `people-${row.id}`,
            error: null,
        })),
    };
};
